import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';

// Icons
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const MessageCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const XCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6" />
    <path d="M9 9l6 6" />
  </svg>
);

const StarIcon = ({ filled = false, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "#ffd700" : "none"} stroke="#ffd700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const UserIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
  </svg>
);

const NavigationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 11 22 2 13 21 11 13 3 11" />
  </svg>
);

// Mock trade detail data
const MOCK_TRADE = {
  id: 'trade1',
  status: 'scheduled', // pending_request, accepted, scheduled, completed, cancelled
  createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),

  // Partner info
  partner: {
    id: 'user123',
    username: 'TrainerRed',
    avatarUrl: null,
    rating: 4.8,
    totalTrades: 24,
    memberSince: '2024-03-15',
  },

  // Your card (the one being requested)
  yourCard: {
    id: 'card1',
    name: 'Charizard VMAX',
    image: 'https://images.pokemontcg.io/swsh35/74.png',
    grade: 'NM',
    set: 'Champion\'s Path',
    rarity: 'Secret Rare',
  },

  // Their card (offered in trade)
  theirCard: {
    id: 'card2',
    name: 'Blastoise EX',
    image: 'https://images.pokemontcg.io/sv1/186.png',
    grade: 'NM',
    set: 'Scarlet & Violet',
    rarity: 'Ultra Rare',
  },

  // Schedule info
  schedule: {
    date: '2024-12-05',
    time: '2:00 PM',
    location: {
      name: 'Pokemon Center NYC',
      address: '10 Rockefeller Plaza',
      city: 'New York',
      state: 'NY',
      zip: '10020',
    },
    user1Confirmed: true,
    user2Confirmed: true,
  },

  // Messages enabled (only after accepted)
  chatEnabled: true,
  unreadMessages: 2,
};

// Card Display Component
const CardDisplay = ({ card, label, theme }) => (
  <div style={styles.cardDisplay}>
    <span style={styles.cardLabel(theme)}>{label}</span>
    <div style={styles.cardImageWrapper}>
      <img
        src={card.image}
        alt={card.name}
        style={styles.cardImage(theme)}
      />
      {card.grade && (
        <div style={styles.cardGrade(theme)}>
          <span style={styles.cardGradeText}>{card.grade}</span>
        </div>
      )}
    </div>
    <span style={styles.cardName(theme)}>{card.name}</span>
    <span style={styles.cardSet(theme)}>{card.set}</span>
    <span style={styles.cardRarity(theme)}>{card.rarity}</span>
  </div>
);

// Partner Info Component
const PartnerInfo = ({ partner, onViewProfile, theme }) => (
  <button style={styles.partnerCard(theme)} onClick={onViewProfile}>
    <div style={styles.partnerAvatar(theme)}>
      {partner.avatarUrl ? (
        <img src={partner.avatarUrl} alt={partner.username} style={styles.avatarImage} />
      ) : (
        <UserIcon />
      )}
    </div>
    <div style={styles.partnerDetails}>
      <span style={styles.partnerName(theme)}>{partner.username}</span>
      <div style={styles.partnerStats}>
        <div style={styles.partnerStat}>
          <StarIcon filled size={14} />
          <span style={styles.partnerStatText(theme)}>{partner.rating}</span>
        </div>
        <span style={styles.partnerStatDivider(theme)}>|</span>
        <span style={styles.partnerStatText(theme)}>{partner.totalTrades} trades</span>
      </div>
    </div>
  </button>
);

// Schedule Info Component
const ScheduleInfo = ({ schedule, onGetDirections, theme }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div style={styles.scheduleCard(theme)}>
      <div style={styles.scheduleHeader}>
        <span style={styles.sectionTitle(theme)}>Meetup Details</span>
        {schedule.user1Confirmed && schedule.user2Confirmed ? (
          <div style={styles.confirmedBadge(theme)}>
            <CheckCircleIcon />
            <span>Both Confirmed</span>
          </div>
        ) : (
          <div style={styles.pendingBadge(theme)}>
            <span>Awaiting Confirmation</span>
          </div>
        )}
      </div>

      <div style={styles.scheduleDetails}>
        <div style={styles.scheduleRow(theme)}>
          <CalendarIcon />
          <span style={styles.scheduleValue(theme)}>{formatDate(schedule.date)}</span>
        </div>
        <div style={styles.scheduleRow(theme)}>
          <ClockIcon />
          <span style={styles.scheduleValue(theme)}>{schedule.time}</span>
        </div>
        <div style={styles.scheduleRow(theme)}>
          <MapPinIcon />
          <div style={styles.locationInfo}>
            <span style={styles.locationName(theme)}>{schedule.location.name}</span>
            <span style={styles.locationAddress(theme)}>
              {schedule.location.address}, {schedule.location.city}, {schedule.location.state}
            </span>
          </div>
        </div>
      </div>

      <button style={styles.directionsButton(theme)} onClick={onGetDirections}>
        <NavigationIcon />
        <span>Get Directions</span>
      </button>
    </div>
  );
};

// Main TradeDetail Component
const TradeDetail = () => {
  const navigate = useNavigate();
  const { tradeId } = useParams();
  const { user } = useAuth();
  const { theme, isDark } = useTheme();

  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrade = async () => {
      setLoading(true);

      // TODO: Replace with real Supabase query
      await new Promise(resolve => setTimeout(resolve, 300));
      setTrade(MOCK_TRADE);

      setLoading(false);
    };

    fetchTrade();
  }, [tradeId, user]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewPartnerProfile = () => {
    navigate(`/user/${trade.partner.id}/collection`);
  };

  const handleOpenChat = () => {
    navigate(`/chat/${tradeId}`);
  };

  const handleGetDirections = () => {
    const { location } = trade.schedule;
    const address = `${location.address}, ${location.city}, ${location.state} ${location.zip}`;
    const mapsUrl = `https://maps.google.com/maps?q=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  };

  const handleCancelTrade = () => {
    if (window.confirm('Are you sure you want to cancel this trade?')) {
      console.log('Cancelling trade:', tradeId);
      // TODO: Implement cancel logic
    }
  };

  const handleConfirmMeetup = () => {
    console.log('Confirming meetup for trade:', tradeId);
    // TODO: Implement confirm logic
    alert('Meetup confirmed!');
  };

  const handleCompleteTrade = () => {
    console.log('Marking trade as complete:', tradeId);
    navigate(`/trade/${tradeId}/review`);
  };

  if (loading) {
    return (
      <div style={styles.container(theme)}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner(theme)} />
        </div>
      </div>
    );
  }

  if (!trade) {
    return (
      <div style={styles.container(theme)}>
        <div style={styles.header(theme)}>
          <button style={styles.backButton(theme)} onClick={handleBack}>
            <ArrowLeftIcon />
          </button>
          <h1 style={styles.headerTitle(theme)}>Trade Not Found</h1>
        </div>
        <div style={styles.errorContainer}>
          <span style={styles.errorText(theme)}>This trade could not be found.</span>
        </div>
      </div>
    );
  }

  const isScheduled = trade.status === 'scheduled';
  const isCompleted = trade.status === 'completed';
  const needsConfirmation = isScheduled && (!trade.schedule.user1Confirmed || !trade.schedule.user2Confirmed);

  return (
    <div style={styles.container(theme)}>
      {/* Header */}
      <div style={styles.header(theme)}>
        <button style={styles.backButton(theme)} onClick={handleBack}>
          <ArrowLeftIcon />
        </button>
        <h1 style={styles.headerTitle(theme)}>Trade Details</h1>
      </div>

      {/* Scrollable Content */}
      <div style={styles.scrollContent}>
        {/* Partner Info */}
        <PartnerInfo
          partner={trade.partner}
          onViewProfile={handleViewPartnerProfile}
          theme={theme}
        />

        {/* Cards Section */}
        <div style={styles.cardsSection(theme)}>
          <CardDisplay card={trade.yourCard} label="Your Card" theme={theme} />
          <div style={styles.swapIndicator}>
            <span style={styles.swapText(theme)}>for</span>
          </div>
          <CardDisplay card={trade.theirCard} label="Their Card" theme={theme} />
        </div>

        {/* Schedule Info (if scheduled) */}
        {isScheduled && trade.schedule && (
          <ScheduleInfo
            schedule={trade.schedule}
            onGetDirections={handleGetDirections}
            theme={theme}
          />
        )}

        {/* Chat Button (if enabled) */}
        {trade.chatEnabled && (
          <button style={styles.chatButton(theme)} onClick={handleOpenChat}>
            <MessageCircleIcon />
            <span>Open Chat</span>
            {trade.unreadMessages > 0 && (
              <div style={styles.chatBadge(theme)}>
                <span style={styles.chatBadgeText(theme)}>{trade.unreadMessages}</span>
              </div>
            )}
          </button>
        )}

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          {needsConfirmation && (
            <button style={styles.confirmButton(theme)} onClick={handleConfirmMeetup}>
              <CheckCircleIcon />
              <span>Confirm Meetup</span>
            </button>
          )}

          {isScheduled && !needsConfirmation && (
            <button style={styles.completeButton(theme)} onClick={handleCompleteTrade}>
              <CheckCircleIcon />
              <span>Mark as Complete</span>
            </button>
          )}

          {!isCompleted && (
            <button style={styles.cancelButton(theme)} onClick={handleCancelTrade}>
              <XCircleIcon />
              <span>Cancel Trade</span>
            </button>
          )}
        </div>

        {/* Trade Timeline/History could go here */}
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.bg,
  }),
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: (theme) => ({
    width: 32,
    height: 32,
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  }),
  errorContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  errorText: (theme) => ({
    fontSize: 16,
    color: theme.textMuted,
  }),

  // Header
  header: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    gap: 12,
    backgroundColor: theme.bg,
    borderBottom: `1px solid ${theme.bgCard}`,
  }),
  backButton: (theme) => ({
    background: 'none',
    border: 'none',
    color: theme.accent,
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  headerTitle: (theme) => ({
    fontSize: 20,
    fontWeight: 700,
    color: theme.text,
    margin: 0,
  }),

  // Scroll Content
  scrollContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    paddingBottom: 100,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },

  // Partner Card
  partnerCard: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: theme.bgCard,
    borderRadius: 12,
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  }),
  partnerAvatar: (theme) => ({
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.border,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.textSecondary,
    overflow: 'hidden',
  }),
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  partnerDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  partnerName: (theme) => ({
    fontSize: 16,
    fontWeight: 600,
    color: theme.text,
  }),
  partnerStats: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  partnerStat: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  partnerStatText: (theme) => ({
    fontSize: 12,
    color: theme.textMuted,
  }),
  partnerStatDivider: (theme) => ({
    color: theme.textSecondary,
  }),

  // Cards Section
  cardsSection: (theme) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 16,
    padding: 20,
    backgroundColor: theme.bgCard,
    borderRadius: 12,
    border: `1px solid ${theme.border}`,
  }),
  cardDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  cardLabel: (theme) => ({
    fontSize: 11,
    fontWeight: 600,
    color: theme.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  }),
  cardImageWrapper: {
    position: 'relative',
  },
  cardImage: (theme) => ({
    width: 100,
    height: 140,
    borderRadius: 6,
    objectFit: 'contain',
    backgroundColor: theme.border,
  }),
  cardGrade: (theme) => ({
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: theme.accent,
    borderRadius: 4,
    padding: '2px 6px',
  }),
  cardGradeText: {
    fontSize: 10,
    fontWeight: 700,
    color: '#000000',
  },
  cardName: (theme) => ({
    fontSize: 13,
    fontWeight: 600,
    color: theme.text,
    textAlign: 'center',
  }),
  cardSet: (theme) => ({
    fontSize: 11,
    color: theme.textMuted,
    textAlign: 'center',
  }),
  cardRarity: (theme) => ({
    fontSize: 10,
    color: theme.accent,
    textAlign: 'center',
  }),
  swapIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginTop: 40,
  },
  swapText: (theme) => ({
    fontSize: 14,
    color: theme.textSecondary,
    fontStyle: 'italic',
  }),

  // Schedule Card
  scheduleCard: (theme) => ({
    padding: 20,
    backgroundColor: theme.bgCard,
    borderRadius: 12,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  }),
  scheduleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: (theme) => ({
    fontSize: 14,
    fontWeight: 600,
    color: theme.text,
  }),
  confirmedBadge: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: theme.success,
    fontSize: 12,
    fontWeight: 500,
  }),
  pendingBadge: (theme) => ({
    backgroundColor: 'rgba(245, 158, 11, 0.15)',
    border: `1px solid ${theme.warning}`,
    borderRadius: 4,
    padding: '4px 8px',
    color: theme.warning,
    fontSize: 11,
    fontWeight: 600,
  }),
  scheduleDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  scheduleRow: (theme) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    color: theme.accent,
  }),
  scheduleValue: (theme) => ({
    fontSize: 14,
    color: theme.text,
  }),
  locationInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  locationName: (theme) => ({
    fontSize: 14,
    color: theme.text,
    fontWeight: 500,
  }),
  locationAddress: (theme) => ({
    fontSize: 12,
    color: theme.textMuted,
  }),
  directionsButton: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 12,
    backgroundColor: `rgba(14, 165, 233, 0.15)`,
    border: `1px solid ${theme.accent}`,
    borderRadius: 8,
    color: theme.accent,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  }),

  // Chat Button
  chatButton: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: 12,
    color: '#000000',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    position: 'relative',
  }),
  chatBadge: (theme) => ({
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: theme.error,
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
  }),
  chatBadgeText: (theme) => ({
    fontSize: 12,
    fontWeight: 700,
    color: theme.text,
  }),

  // Action Buttons
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 8,
  },
  confirmButton: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    border: `1px solid ${theme.success}`,
    borderRadius: 12,
    color: theme.success,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  }),
  completeButton: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    border: `1px solid ${theme.success}`,
    borderRadius: 12,
    color: theme.success,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  }),
  cancelButton: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.error}`,
    borderRadius: 12,
    color: theme.error,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  }),
};

export default TradeDetail;
